/* eslint-disable */
/**
 * validatePrefundSLA — pure SLA decision function for the Prefund SLA validation step.
 * Mirrors the existing validateItemLimits.ts pattern: no side effects, fully
 * unit-testable.
 *
 * The fudge-factor math (totalWithFudge) is owned exclusively by
 * calcTotalPricesWithFudgeFactor in NationalPartnerProvider — this function accepts the
 * already-computed totalWithFudge plus the two limits and only owns the comparison
 * against StorePrefundMaxAmount / DMPrefundMaxAmount.
 *
 * IMPORTANT — no hardcoded default limits: per the story's acceptance criteria, the
 * store max and DM max amounts must come exclusively from the config service response.
 * There is no fallback amount to compare against. If either limit could not be
 * retrieved from config (null/undefined/NaN), this function returns
 * 'CONFIG_UNAVAILABLE' — the caller must treat that as a hard block (Confirm Request
 * stays disabled) rather than assume the request is within limits.
 */

export type PrefundSLADecision =
  | 'WITHIN_STORE_LIMIT'
  | 'REQUIRES_MANAGER_PIN'
  | 'EXCEEDS_DM_LIMIT'
  | 'CONFIG_UNAVAILABLE';

export interface PrefundSLAValidation {
  decision: PrefundSLADecision;
  totalWithFudge: number;
  storeLimit: number | null;
  dmLimit: number | null;
}

/**
 * Validates the pre-computed totalWithFudge amount against the store and DM prefund
 * limits retrieved from the config service:
 * - either limit missing/invalid        -> CONFIG_UNAVAILABLE (hard block, cannot proceed)
 * - totalWithFudge <= storeLimit         -> WITHIN_STORE_LIMIT  (proceed, no SLA)
 * - storeLimit < totalWithFudge <= dmLimit -> REQUIRES_MANAGER_PIN (manager PIN override)
 * - totalWithFudge > dmLimit              -> EXCEEDS_DM_LIMIT   (hard block, no override)
 *
 * @param totalWithFudge Already-computed total cost including the variance/fudge factor
 * @param storePrefundMaxAmount Store-level prefund max amount from config, or null/NaN if unavailable
 * @param dmPrefundMaxAmount DM-level prefund max amount from config, or null/NaN if unavailable
 * @returns Validation result carrying the decision plus the values compared
 */
export function validatePrefundSLA(
  totalWithFudge: number,
  storePrefundMaxAmount: number | null | undefined,
  dmPrefundMaxAmount: number | null | undefined,
): PrefundSLAValidation {
  const storeLimitValid =
    storePrefundMaxAmount !== null &&
    storePrefundMaxAmount !== undefined &&
    !Number.isNaN(storePrefundMaxAmount);
  const dmLimitValid =
    dmPrefundMaxAmount !== null &&
    dmPrefundMaxAmount !== undefined &&
    !Number.isNaN(dmPrefundMaxAmount);

  if (!storeLimitValid || !dmLimitValid) {
    return {
      decision: 'CONFIG_UNAVAILABLE',
      totalWithFudge,
      storeLimit: storeLimitValid ? (storePrefundMaxAmount as number) : null,
      dmLimit: dmLimitValid ? (dmPrefundMaxAmount as number) : null,
    };
  }

  const storeLimit = storePrefundMaxAmount as number;
  const dmLimit = dmPrefundMaxAmount as number;

  if (dmLimit < storeLimit) {
return {
decision: 'CONFIG_UNAVAILABLE',
totalWithFudge,
storeLimit,
dmLimit,
};
}

  let decision: PrefundSLADecision;
  if (totalWithFudge <= storeLimit) {
    decision = 'WITHIN_STORE_LIMIT';
  } else if (totalWithFudge <= dmLimit) {
    decision = 'REQUIRES_MANAGER_PIN';
  } else {
    decision = 'EXCEEDS_DM_LIMIT';
  }

  return {
    decision,
    totalWithFudge,
    storeLimit,
    dmLimit,
  };
}
